// Search state and DataGrid wiring of the historic tickets page: committed native search, the
// optional 'Totale e gruppi' mode, request guards and layout storing. Shared by the page and its
// synthetic browser fixture, so the fixture exercises this exact production code.
import { RefObject, useCallback, useMemo, useRef, useState } from 'react'
import { Switch, TextBox, Toolbar as DxToolbar } from 'devextreme-react'
import { Item as ToolbarItem } from 'devextreme-react/toolbar'
import { Button as TextBoxButton, TextBoxRef } from 'devextreme-react/text-box'
import { DataGridRef, DataGridTypes } from 'devextreme-react/data-grid'
import ODataStore from 'devextreme/data/odata/store'
import notify from 'devextreme/ui/notify'
import { createHistoricBaseFilter } from './historicSearchFilter'
import {
  createHistoricBeforeSend,
  historicTicketUrl,
  nativeHistoricErrorMessage,
  normalizeNativeHistoricSearch,
} from './historicNativeSearch'
import {
  createGridLayoutStore,
  createGridStateStoring,
  HISTORIC_GRID_STORAGE_KEY,
  StateStorage,
} from './historicGridState'
import { createCurrentToken } from './historicToken'
import {
  COUNT_TOGGLE_LABEL,
  historicSearchBoxWidth,
  createCountedSessionGuard,
  createGenerationGate,
  LiveGrid,
  historicGridKey,
  historicGridMode,
  historicGridModeOptions,
  isHistoricGuardRefusal,
  layoutModeOf,
  liveFilterSignature,
  RetiredSearchGridError,
  stateWithLiveFilterOptions,
} from './historicSearchMode'

/** The request object DevExtreme's ODataStore passes to beforeSend. */
export type HistoricRequest = {
  url: string
  async: boolean
  method: string
  timeout: number
  params: Record<string, unknown>
  payload: unknown
  headers: Record<string, unknown>
}
/** The app's ODataStoreRequestConfiguration, or a synthetic one in the fixture. */
export type RequestConfiguration = (bearer: string, timeoutMs: number) => (request: HistoricRequest) => HistoricRequest

export const HISTORIC_EXPAND = [
  'sede($expand=azienda($select=id,nome);$select=nome,azienda)',
  'tipologia_ticket($select=nome)',
  'assegnato($select=fullname)',
  'tipologia_problema($select=nome)',
  'argomento_problema($select=nome)',
  'piattaforma($select=nome)',
  'applicazione($select=nome)',
  'creatore($select=fullname)',
  'eventi($expand=utente($select=fullname), tipologia($select=nome))',
  'stato',
]

export const useHistoricSearchGrid = (options: {
  host: string
  token: unknown
  gridRef: RefObject<DataGridRef>
  storage: StateStorage | undefined
  configure: RequestConfiguration
}) => {
  const { host, token, gridRef, storage, configure } = options

  // Committed, trimmed free-text search. A non-empty value switches the grid to search mode:
  // infinite scrolling, no exact count, no grouping. Clearing it returns to normal browsing.
  const [committedSearch, setCommittedSearch] = useState<string | null>(null)
  const isSearchMode = committedSearch !== null
  // Explicit opt-in 'Totale e gruppi' for the committed search; OFF by default and after any change
  // of text or filters.
  const [countRequested, setCountRequested] = useState(false)
  const mode = historicGridMode(committedSearch, countRequested)
  const layoutMode = layoutModeOf(mode)
  // Every grid replacement advances this page-wide monotonic generation SYNCHRONOUSLY (before any
  // React state change): requests, layout saves and stale callbacks of earlier generations are
  // refused or ignored from that moment, even while DevExtreme still holds the old store.
  const [gate] = useState(createGenerationGate)
  const [generation, setGeneration] = useState(gate.current)
  const gridKey = historicGridKey(mode, generation)
  // Logical user filter consented to at the toggle, for the counted generation it created.
  const consentRef = useRef<{ generation: number; consent: string } | null>(null)

  // The live DataGrid instance, if mounted (public API: state, option, columnCount, columnOption).
  const liveGrid = useCallback((): LiveGrid | undefined => {
    try {
      return gridRef.current?.instance() ?? undefined
    } catch {
      return undefined
    }
  }, [gridRef])

  // Requests read the current token at send time, so a normal token refresh never rebuilds or
  // reloads the data source. It is updated during render, before the grid can mount or send, and
  // the grid is shown only while the current token is usable (non-empty); otherwise it fails closed.
  const [currentToken] = useState(createCurrentToken)
  currentToken.set(token)
  const hasToken = currentToken.isReady()

  // The page's source of truth for the browse layout, independent of DevExtreme's delayed saves.
  const [layoutStore] = useState(() => createGridLayoutStore(storage, HISTORIC_GRID_STORAGE_KEY))

  /**
   * Replaces the grid: retires the current generation synchronously, then captures the CURRENT
   * grid's layout (including changes DevExtreme has not saved yet) and returns the new generation.
   * Called only from handlers of the current generation (user actions or its own guard).
   */
  const replaceGrid = (fromLayoutMode: 'browse' | 'search'): number => {
    const next = gate.advance()
    layoutStore.save(fromLayoutMode, stateWithLiveFilterOptions(liveGrid()))
    return next
  }

  // Requests of this generation's store only; a retired store's requests are refused unsent.
  const admit = useMemo(() => {
    const own = generation
    return () => {
      if (!gate.isCurrent(own)) throw new RetiredSearchGridError()
    }
  }, [gate, generation])

  // ON: every counted request is admitted only while the grid's logical user filter equals the one
  // consented to at the toggle. A change (even before the first counted request) is refused unsent,
  // the layout with the new filter is kept, and the search continues on the fast path (OFF).
  const countedGuard = useMemo(() => {
    const own = generation
    const consent = consentRef.current
    if (mode !== 'counted' || consent?.generation !== own) return undefined
    return createCountedSessionGuard({
      consent: consent.consent,
      currentSignature: () => liveFilterSignature(liveGrid()),
      isCurrent: () => gate.isCurrent(own),
      onStale: () => {
        const next = replaceGrid('search')
        setCountRequested(false)
        setGeneration(next)
      },
    })
    // One guard per counted generation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, generation])

  const dataSource = useMemo(() => {
    const url = historicTicketUrl(host, committedSearch)
    return {
      store: new ODataStore({
        url,
        key: 'id',
        keyType: 'Int32',
        version: 4,
        errorHandler: (e) => {
          // Guard refusals are deliberate: the page already switched mode or replaced the grid.
          if (isHistoricGuardRefusal(e)) return
          if (isSearchMode) {
            notify(nativeHistoricErrorMessage(e), 'error', 5000)
            return
          }
          console.error(e.errorDetails)
          if (!e.errorDetails) return
          notify(
            {
              message: `Errore : ${e.errorDetails?.message}`,
              type: 'error',
              displayTime: 5000,
            },
            {
              position: 'bottom center',
              direction: 'up-push',
            },
          )
        },
        // Checks the current token at send time and refuses to send without a usable one.
        // Only the counted mode may add $count, and only while its filter is unchanged.
        beforeSend: createHistoricBeforeSend(
          currentToken,
          configure,
          url,
          isSearchMode,
          countedGuard && { beforeRequest: countedGuard.beforeRequest },
          admit,
        ),
        deserializeDates: false,
      }),
      expand: HISTORIC_EXPAND,
      // The grid decides whether an exact count is needed: browse and counted modes ask for one,
      // the fast search (infinite scrolling) does not.
      // The function owns ONLY committed global text; grid field/header filters still compose normally.
      filter: createHistoricBaseFilter(),
    }
  }, [admit, committedSearch, configure, countedGuard, currentToken, host, isSearchMode])

  // Search and counted grids never store their grouping/first page over the browse layout. Only the
  // current generation may save; a replaced grid's delayed save is ignored.
  const stateStoring = useMemo(() => {
    const own = generation
    return createGridStateStoring(layoutMode, layoutStore, () => gate.isCurrent(own))
  }, [gate, generation, layoutMode, layoutStore])

  // Changing the search replaces the grid. Its current layout is captured first, so changes that
  // DevExtreme has not saved yet are kept.
  // The search box's visible text, owned by the page: toolbar re-renders or remounts (toggle shown or
  // hidden, mode or token changes) must not drop what the user typed, and clearing it must always
  // be a real change that commits "no search".
  const [searchDraft, setSearchDraft] = useState('')

  const commitSearch = (value: unknown) => {
    setSearchDraft(typeof value === 'string' ? value : '')
    let next: string | null
    try {
      next = normalizeNativeHistoricSearch(value)
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Ricerca non valida.', 'error', 5000)
      return
    }
    if (next === committedSearch) return
    const generationAfter = replaceGrid(layoutMode)
    setCountRequested(false)
    setCommittedSearch(next)
    setGeneration(generationAfter)
  }

  // ON: a new counted grid, ungrouped on its first page. OFF: back to the fast search, ungrouped on
  // its first page; the browse grouping/paging stay as saved.
  const toggleCount = (on: boolean) => {
    if (on === countRequested || !isSearchMode) return
    // Consent = the logical user filter of the grid the user is looking at, read before retiring it.
    const consent = liveFilterSignature(liveGrid())
    const next = replaceGrid(layoutMode)
    consentRef.current = on ? { generation: next, consent } : null
    setCountRequested(on)
    setGeneration(next)
  }

  // Stable per mode, so ordinary re-renders (for example the periodic token refresh) do not
  // hand the grid new option objects that would reset its columns or data.
  const gridModeOptions = useMemo(() => {
    const modeOptions = historicGridModeOptions(mode)
    return {
      ...modeOptions,
      customizeColumns: (columns: DataGridTypes.Column[]) => {
        // Grouping during the fast search would reintroduce exact counts and full loads.
        if (modeOptions.disableGrouping) columns.forEach((column) => (column.allowGrouping = false))
      },
    }
  }, [mode])

  return {
    committedSearch,
    isSearchMode,
    mode,
    gridKey,
    countRequested,
    hasToken,
    dataSource,
    stateStoring,
    gridModeOptions,
    commitSearch,
    searchDraft,
    toggleCount,
  }
}

/** Compact, labelled opt-in shown left of the search box only while a search is committed.
 * `compact` (extra-small screens) hides the visible text only; the accessible name and tooltip stay. */
export const HistoricCountToggle = (props: { value: boolean; onToggle: (on: boolean) => void; compact?: boolean }) => (
  <label
    title={COUNT_TOGGLE_LABEL}
    // Explicit inline styles (no utility classes): switch and caption on one row, aligned with the box.
    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, margin: '0 8px 0 0', whiteSpace: 'nowrap' }}
  >
    <Switch
      value={props.value}
      hint={COUNT_TOGGLE_LABEL}
      elementAttr={{ 'aria-label': COUNT_TOGGLE_LABEL, 'data-testid': 'historic-count-toggle' }}
      onValueChanged={(e) => {
        // Only user interaction toggles; programmatic resets (e.g. a new search) do not re-enter.
        if (e.event) props.onToggle(e.value === true)
      }}
    />
    {/* Compact (extra-small): switch only; the Switch keeps the full aria-label and tooltip. */}
    {!props.compact && <span>{COUNT_TOGGLE_LABEL}</span>}
  </label>
)

/**
 * The page's search toolbar: 'Totale e gruppi' left of the search box. Both toolbar items always
 * exist (only the toggle's visibility changes), and the box shows the page-owned draft, so toolbar
 * re-renders never remount or clear what the user typed, and clearing always commits "no search".
 */
export const HistoricSearchToolbar = (props: {
  isXSmall: boolean
  isSearchMode: boolean
  countRequested: boolean
  searchDraft: string
  onToggle: (on: boolean) => void
  onCommit: (value: unknown) => void
}) => {
  const refSearch = useRef<TextBoxRef>(null)
  return (
    <DxToolbar>
      <ToolbarItem location="after" locateInMenu="never" visible={props.isSearchMode}>
        <HistoricCountToggle value={props.countRequested} onToggle={props.onToggle} compact={props.isXSmall} />
      </ToolbarItem>
      <ToolbarItem location="after" locateInMenu="never">
        <TextBox
          width={historicSearchBoxWidth(props.isXSmall, props.isSearchMode)}
          ref={refSearch}
          placeholder="Parole intere o frasi tra virgolette"
          showClearButton={true}
          value={props.searchDraft}
          elementAttr={{ 'data-testid': 'historic-search' }}
          onValueChanged={(e) => props.onCommit(e.value)}
        >
          <TextBoxButton
            name="search"
            location="after"
            options={{
              icon: 'search',
              stylingMode: 'text',
              onClick: () => {
                refSearch.current?.instance().option('focus', false)
              },
            }}
          />
          <TextBoxButton name="clear" />
        </TextBox>
      </ToolbarItem>
    </DxToolbar>
  )
}
